import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatMessage, ChatbotService } from '@app/_core/services/chatbot.service';
import { AppService } from '@app/_core/services/app.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-chatbot',
    templateUrl: './chatbot.component.html',
    styleUrls: ['./chatbot.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ChatbotComponent implements OnInit, OnDestroy {
    isOpen: boolean = false;
    isMinimized: boolean = false;
    inputText: string = '';
    isTyping: boolean = false;
    messages: any[] = [];
    chatTitle: string = 'AI Assistant';
    activeTab: 'transcript' | 'ai-suggestion' = 'transcript';
    showTranscript: boolean = false;
    isVoiceEnabled: boolean = false;
    followUps: string[] = [];
    suggestedQuestions: string[] = [];

    private titleSub!: Subscription;
    private openTabSub!: Subscription;
    private scrollToBottomTimeout: any;

    @ViewChild('messageList') private messageList!: ElementRef<HTMLDivElement>;

    constructor(private chatbotService: ChatbotService, private appService: AppService,
        private sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.appService.TranscriptEvents.subscribe((isDisabled: boolean) => {
            this.showTranscript = !isDisabled;
        });
        this.titleSub = this.chatbotService.chatTitle$.subscribe(title => {
            this.chatTitle = title;
        });
        this.openTabSub = this.chatbotService.openToTab$.subscribe(tab => {
            if (tab) {
                this.isOpen = true;
                this.isMinimized = false;
                this.activeTab = tab;
            }
        });
    }

    ngOnDestroy(): void {
        this.titleSub?.unsubscribe();
        this.openTabSub?.unsubscribe();
        this.scrollToBottomTimeout && clearTimeout(this.scrollToBottomTimeout);
    }

    firstName(): string {
        if (this.chatTitle.startsWith('Call with ')) {
            return this.chatTitle.replace('Call with ', '').trim();
        }
        return 'User';
    }

    toggleChat(): void {
        if (this.isOpen && !this.isMinimized) {
            // Close completely
            this.isOpen = false;
            this.isMinimized = false;
        } else if (this.isOpen && this.isMinimized) {
            // Restore from minimized
            this.isMinimized = false;
        } else {
            // Open fresh
            this.isOpen = true;
            this.isMinimized = false;

            if (this.messages.length === 0) {
                this.pushBotMessage({
                    title: "",
                    sections: [
                        { heading: "", content: ["Hello! How can I assist you today?"] }
                    ],
                    lists: [],
                    followUps: [
                        
                    ]
                });
            }

        }
    }

    toggleMinimize(): void {
        this.isMinimized = !this.isMinimized;
    }

    sendMessage(): void {
        const text = this.inputText.trim();
        if (!text || this.isTyping) { return; }

        //this.messages.push({ role: 'user', text, timestamp: new Date() });
        this.pushUserMessage(text);
        this.inputText = '';
        this.isTyping = true;
        this.scrollToBottom();
        this.followUps = [];


        const intent = this.detectIntent(text);
        this.isTyping = false;
        // Greeting
        if (intent === 'greeting') {
            this.pushBotMessage(this.createSimpleBotMessage(
                "Hello! How can I assist you today?",
                ["Ask about call scripts", "Need help with HIPAA validation?"]
            ));
            return;
        }

        // Smalltalk
        if (intent === 'smalltalk') {
            this.pushBotMessage(this.createSimpleBotMessage(
                "I am your AI assistant to help with call center knowledge base queries.",
                ["Ask about transfer process", "Check HIPAA verification steps"]
            ));
            return;
        }

        
        this.chatbotService.sendMessage(text, this.messages).subscribe({
            next: (reply: any) => {
                
                this.pushBotMessage(reply.Response.Data);
                console.log('KnowledgeBot response', reply.Response.Data)
            },
            error: () => {
                //this.isTyping = false;
                this.pushBotMessage('Sorry, something went wrong. Please try again.');
            }
        });
    }

    private pushBotMessage(response: any): void {

        let parsed: any;

        try {
            //  Handle object or string response
            if (typeof response === 'string') {

                //  Clean JSON (fix common LLM issues)
                var cleaned = this.cleanJsonString(response);
                cleaned = cleaned.replace(/"\s*"/g, '"');
                parsed = JSON.parse(cleaned);
            } else {
                parsed = response;
            }

        } catch (err) {
            console.error('? Invalid JSON from LLM:', response);

            //  Fallback (VERY IMPORTANT)
            parsed = {
                title: "",
                sections: [
                    {
                        heading: "",
                        content: [typeof response === 'string' ? response : 'Unable to parse response']
                    }
                ],
                lists: [],
                followUps: []
            };
        }

        const html = this.formatBotResponse(parsed);

        this.messages.push({
            role: 'bot',
            text: html,
            timestamp: new Date()
        });

        // Safe data check
        const hasData =
            parsed &&
            (
                (parsed.title && parsed.title.trim().length > 0) ||
                (parsed.sections && parsed.sections.length > 0) ||
                (parsed.lists && parsed.lists.length > 0)
            );

        // Follow-ups fallback
        this.followUps = hasData
            ? (parsed.followUps || [])
            : [
                "Can you rephrase your question?",
                "Try asking something else"
            ];

        //  Scroll after rendering
        this.scrollToBottom();
    }
    private cleanJsonString(text: string): string {
        return text
            //  Fix proper smart quotes
            .replace(/[��]/g, '"')

            //  Fix corrupted characters (? replacement)
           // .replace(/?/g, '"')

            //  Fix HTML encoding
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')

            //  Fix double quotes issues
            .replace(/""/g, '"')

            // Fix improper escaping
            .replace(/\\+"/g, '\\"')

            //  Fix broken end quotes before ] or }
            .replace(/\\+"(\s*[\]\}])/g, '"$1')

            //  Normalize spaces
            .replace(/\s+/g, ' ')
            .trim();
    }


    // Format JSON ? HTML
    formatBotResponse(data: any): SafeHtml {

        if (!data || (!data.title && !data.sections?.length && !data.lists?.length)) {
            return this.getNoDataHtml();
        }

        let html = `<div class="chatbot-content">`;

        if (data.title) {
            html += `<h4 class="chatbot-title">${data.title}</h4>`;
        }

        if (data.sections) {
            data.sections.forEach((sec: any) => {
                html += `
          <div class="chatbot-section">
            <strong>${sec.heading}</strong>
            <ul>${sec.content.map((c: string) => `<li>${c}</li>`).join('')}</ul>
          </div>
        `;
            });
        }

        if (data.lists) {
            data.lists.forEach((list: any) => {
                html += `
          <div class="chatbot-section">
            <strong>${list.heading}</strong>
            <ol>${list.items.map((i: string) => `<li>${i}</li>`).join('')}</ol>
          </div>
        `;
            });
        }

        html += `</div>`;

        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    private getNoDataHtml(): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(`
      <div class="chatbot-content chatbot-empty">
        <p>Sorry, I could not find an exact answer.</p>
      </div>
    `);
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    pushUserMessage(text: string) {
        this.messages.push({
            role: 'user',
            text: text,
            timestamp: new Date()
        });
        this.scrollToBottom();
    }

    private createSimpleBotMessage(text: string, followUps: string[]) {
        return {
            title: "",
            sections: [{ heading: "", content: [text] }],
            lists: [],
            followUps
        };
    }

    //private pushBotMessage(text: string): void {
    //    this.messages.push({ role: 'bot', text, timestamp: new Date() });
    //    this.scrollToBottom();
    //}

    private scrollToBottom(): void {
        this.scrollToBottomTimeout = setTimeout(() => {
            if (this.messageList) {
                this.messageList.nativeElement.scrollTop = this.messageList.nativeElement.scrollHeight;
            }
        }, 50);
    }


    sendFollowup(text: string) {
        this.inputText = text;
        this.sendMessage();
    }

    private detectIntent(text: string): 'greeting' | 'smalltalk' | 'kb' {

        let msg = text.toLowerCase().trim();

        // Normalize: remove punctuation
        msg = msg.replace(/[^\w\s]/g, '');

        const greetings = ['hi', 'hello', 'hey'];
        const greetingPhrases = ['good morning', 'good evening'];

        const smallTalkExact = [
            'what is your name',
            'whats your name',
            'your name',
            'who are you',
            'tell me about yourself'
        ];

        const smallTalk = [
            'how are you',
            'thanks',
            'thank you',
            'bye',
            'ok'
        ];

        if (greetings.includes(msg) || greetingPhrases.includes(msg)) {
            return 'greeting';
        }

        if (smallTalkExact.some(s => msg.includes(s))) {
            return 'smalltalk';
        }

        if (smallTalk.some(s => msg.includes(s))) {
            return 'smalltalk';
        }

        return 'kb';
    }

}
